<template>
  <!-- Contenedor Padre Fullscreen (Fijo a los bordes de la pantalla para evitar scroll de App.vue) -->
  <div v-if="!cargandoComponente && permitirVenta" class="fixed zapi-pos-pantalla left-0 w-full m-0 p-2 zapi-bg flex flex-row overflow-hidden select-none z-5 gap-3">

    <!-- SECCIÓN IZQUIERDA: HEADER, CATÁLOGO Y CATEGORÍAS -->
    <div class="flex-1 flex flex-column h-full overflow-hidden py-3 pl-3 pr-0" style="min-width: 0;">

      <!-- ORGANISMO: BARRA SUPERIOR (HEADER) -->
      <ZapiHeader class="flex-shrink-0 z-5 mb-2" v-model:busqueda="busquedaProducto" :suggestions="productosFiltrados"
        :nombreSucursal="caja?.nombre_sucursal || 'Desconocida'"
        :logoLocal="empresaGlobal ? empresaGlobal.logo : null"
        @buscarProducto="buscarProducto" @reportarMerma="abrirModalMerma">
        <template #acciones>
          <!-- Etapa 3C: cola de ventas (botón compacto «Ventas») -->
          <ZapiEstadoSync class="flex-shrink-0" />
          <!-- Impresora local: círculo rojo si hay que conectarla, naranja mientras busca, verde si está conectada (2.4rem, ícono blanco) -->
          <Button v-if="estadoImpresora !== 'conectado'"
            :icon="estadoImpresora === 'verificando...' ? 'pi pi-spin pi-spinner' : 'pi pi-print'"
            class="p-button-rounded p-button-sm flex-shrink-0" data-testid="pos-impresora" :class="estadoImpresora === 'verificando...' ? 'p-button-warning' : 'p-button-danger'"
            style="width: 2.4rem; height: 2.4rem;"
            v-tooltip.bottom="estadoImpresora === 'verificando...' ? 'Buscando impresora…' : 'Impresora desconectada — clic para conectar'"
            @click="mostrarModalImpresora = true; buscarImpresoras();" />
          <!-- Ajuste visual 2: el verde sólido reemplaza al gris de la decisión D1 (a). Antes:
          <Button v-else icon="pi pi-print" class="p-button-rounded p-button-sm p-button-text p-button-secondary"
            v-tooltip.bottom="'Impresora conectada: clic para cambiar'"
            @click="mostrarModalImpresora = true; buscarImpresoras();" /> -->
          <Button v-else icon="pi pi-print" class="p-button-rounded p-button-sm p-button-success flex-shrink-0" data-testid="pos-impresora"
            style="width: 2.4rem; height: 2.4rem;"
            v-tooltip.bottom="'Impresora conectada: clic para cambiar'"
            @click="mostrarModalImpresora = true; buscarImpresoras();" />
        </template>
      </ZapiHeader>

      <!-- MOLÉCULA: PESTAÑAS DE CATEGORÍAS -->
      <ZapiCategoryTabs v-model="categoriaActiva" :categorias="gruposNombres" />
      <div v-if="subgruposNombres.length > 0" class="subgrupo-wrapper">
        <p class="subgrupo-label">
          <i class="pi pi-level-down-right" style="font-size: 0.8rem"></i> Subcategoría de {{ categoriaActiva }}
        </p>
        <ZapiCategoryTabs is-sub v-model="subCategoriaActiva" :categorias="subgruposNombres" />
      </div>

      <!-- GRID DE PRODUCTOS CON SCROLL INTERNO CONTROLADO POR VH -->
      <div class="grid overflow-y-auto flex-1 align-content-start mt-0 mb-0 pt-3 pb-4 pos-grid-container"
        style="margin-left: -0.5rem; margin-right: -0.5rem;">
        <!-- Añadimos 'flex align-items-stretch' para que todas las columnas midan lo mismo de alto -->
        <div v-for="producto in productosPorCategoria" :key="claveCatalogo(producto)"
          class="col-12 sm:col-6 md:col-4 lg:col-3 zapi-card-5 p-2 flex align-items-stretch zapi-card-wrapper">
          <!-- 'w-full' obliga a la tarjeta a rellenar todo el espacio de su celda perfectamente -->
          <ZapiProductCard 
          :producto="producto"
          :stockActual="producto.stock_actual"
          :stockMinimo="producto.stock_minimo"
          @seleccionar="seleccionarPizza" 
          class="w-full" />
        </div>
      </div>
    </div>

    <!-- SECCIÓN DERECHA: PANEL DE CONTROL DE FACTURACIÓN Y CARRITO -->
    <div class="flex-shrink-0 bg-white border-round shadow-1 p-4 flex flex-column overflow-hidden mt-3 mb-3 mr-3"
      style="width: clamp(28rem, 35vw, 45rem);">

        <!-- MOLÉCULA: FORMULARIO DEL CLIENTE -->
        <ZapiClientForm v-model:cliente="clienteSeleccionado" v-model:nit="nitCliente" :suggestions="clientesFiltrados"
          @buscarCliente="buscarCliente"
          @clienteSeleccionado="seleccionarCliente"
          @nuevoCliente="abrirModalCliente" />

      <!-- ORGANISMO: TABLA DEL CARRITO (SCROLL INDEPENDIENTE USANDO FLEX-1) -->
      <div class="mb-2 flex-1 overflow-y-auto pos-cart-table-container">
        <!-- TABLA DEL CARRITO: Pasamos eventos de eliminar y editar -->
        <ZapiCartTable 
          :carrito="carrito" 
          :salsasDisponibles="modalProductoSalsa"
          @editarCantidad="abrirNumpad" 
          @eliminar="eliminarDelCarrito"
          @editarSalsas="abrirEdicionSalsas" 
          @sumarCantidad="sumarCantidadCarrito" 
          @restarCantidad="restarCantidadCarrito" 
        />
      </div>

      <!-- PANEL DE TOTALES, ORIGEN Y MÉTODOS DE PAGO ANCLADO ABAJO -->
      <div class="mt-auto mb-1 border-top-1 surface-border pt-2 bg-gray-50 p-2 border-round flex-shrink-0">
        <!-- SUB TOTAL Y DESCUENTO -->
        <div class="flex justify-content-between mb-1 align-items-center">
          <span class="text-600 font-bold">Subtotal</span>
          <span class="font-medium text-900 text-lg">Bs. {{ totalCalculado }}</span>
        </div>

        <!-- PROMOCIONES DINÁMICAS -->
        <div v-if="descuentoTotalPromocional > 0" class="flex justify-content-between mb-1 align-items-center bg-yellow-50 p-2 border-round">
          <div class="flex flex-column">
            <span class="text-yellow-700 font-bold"><i class="pi pi-star-fill text-yellow-500 mr-1"></i> Descuento de Combo(s)</span>
            <small class="text-yellow-600" v-for="(p, i) in desglosePromociones" :key="i">
              {{ p.promocion }}
            </small>
          </div>
          <span class="font-bold text-red-500 text-lg">- Bs. {{ descuentoTotalPromocional }}</span>
        </div>
        
        <div class="flex justify-content-between mb-2 align-items-center" v-if="false">
          <span class="text-600 font-bold">Descuento (Bs.)</span>
          <div class="p-inputgroup" style="width: 120px;">
            <span class="p-inputgroup-addon p-1 text-sm font-bold">Bs.</span>
            <input type="number" class="p-inputtext p-component p-inputtext-sm text-right font-bold" v-model.number="descuentoManual" placeholder="0" min="0" />
          </div>
        </div>
        
        <div class="flex justify-content-between align-items-center mb-2 border-top-1 border-300 pt-2">
          <span class="text-xl font-bold text-900">Total a Pagar</span>
          <span class="text-3xl zapi-font zapi-text-red">Bs. {{ totalAPagar }}</span>
        </div>

        <!-- MOLÉCULA: SELECTOR DE ORIGEN -->
        <ZapiOriginSelector v-model="canalVenta" @metodos-disponibles="actualizarMetodosPago" :canalesPermitidos="canalesPermitidosCarrito" :canales="canalesPos" />

        <!-- MOLÉCULA: SELECTOR DE MÉTODOS DE PAGO EXPLÍCITOS -->
        <ZapiPaymentSelector v-model="metodoPago" :metodos="metodosDisponibles" />

        <!-- OBSERVACIÓN -->
        <div class="mt-3 mb-3">
          <input type="text" class="p-inputtext p-component w-full" v-model="observacionVenta" placeholder="Observación (opcional)" />
        </div>

        <!-- ACCIÓN PRINCIPAL DEL POS -->
        <!-- data-testid: pruebas automáticas (e2e/) -->
        <Button label="Preparar" icon="pi pi-print" data-testid="pos-preparar"
          class="w-full p-button-lg zapi-bg-red zapi-font text-xl py-2 shadow-2 border-none border-round-xl" @click="prepararVenta"
          :loading="procesando"
          :disabled="carrito.length === 0 || procesando" />
      </div>

    </div>


    <!-- ORGANISMO MODAL: CONFIGURACIÓN DE LAS SALSAS Y EXTRAS DINÁMICOS -->
    <ZapiModalPizzaConfig 
      v-model:visible="mostrarModalPizza" 
      :pizza="pizzaEnEdicion"
      :salsasDisponibles="modalProductoSalsa"
      :catalogoPizzas="catalogoProductos"
      :sucursalId="sucursal_id"
      @confirmar="confirmarAgregarAlCarrito"
      @cancelar="mostrarModalPizza = false" />

    <!-- ORGANISMO MODAL: TECLADO NUMÉRICO INTEGRADO DE CANTIDADES -->
    <ZapiModalNumpad v-model:visible="mostrarNumpad" :valorInicial="cantidadTemporal" @confirmar="confirmarNumpad"
      @cancelar="mostrarNumpad = false" />

    <!-- MODAL PARA CREAR UN NUEVO CLIENTE -->
    <ClienteCreate
      :show="mostrarModalCliente"
      :cliente="clienteParaModal"
      @closeModal="cerrarModalCliente"
      @actualizarListado="actualizarListadoClientes"
    />

    <!-- MODAL CONFIRMACIÓN DE VENTA -->
    <ZapiModalConfirmacionVenta
      v-model:visible="mostrarModalConfirmacion"
      :total="totalAPagar"
      :loading="procesando"
      :metodoPrincipalId="metodoPago"
      :nombreMetodoPrincipal="metodosDisponibles.find(m => m.value === metodoPago)?.label || 'Digital'"
      @confirm="procesarVenta"
    />

    <!-- MODAL SMART COMBO -->
    <ZapiModalSmartCombo
      v-model:visible="mostrarModalSmartCombo"
      :promoId="promoActivaId"
      :promoData="promoActiva"
      :canalVentaId="canalVenta"
      :catalogoTotal="catalogoCombos"
      :grupos="rawGruposData"
      @confirmar="confirmarSmartCombo"
      @cancelar="mostrarModalSmartCombo = false; promoActivaId = null"
    />

    <!-- MODAL CONFIGURACIÓN IMPRESORA LOCAL -->
    <Dialog v-model:visible="mostrarModalImpresora" modal header="Configuración de Impresora Local" :style="{ width: '400px' }">
      <div class="flex flex-column gap-3 pt-2">
        <div class="text-center">
          <i class="pi pi-print text-4xl mb-2 text-primary"></i>
          <p class="m-0 text-secondary">Seleccione la impresora térmica que usará esta caja.</p>
        </div>
        <div class="flex flex-column gap-2">
          <label class="font-bold">Impresoras Detectadas:</label>
          <Dropdown v-model="impresoraSeleccionada" :options="listaImpresoras" optionLabel="name" placeholder="Seleccionar Impresora" class="w-full">
            <template #empty>
              No se detectaron impresoras USB o el servicio no está activo.
            </template>
          </Dropdown>
        </div>
      </div>
      <template #footer>
        <Button label="Test Impresión" icon="pi pi-print" class="p-button-secondary" @click="probarImpresionBackend" v-if="true" />
        <Button label="Cancelar" icon="pi pi-times" text @click="mostrarModalImpresora = false" />
        <Button label="Guardar" icon="pi pi-check" @click="guardarImpresora(impresoraSeleccionada)" :disabled="!impresoraSeleccionada" autofocus />
      </template>
    </Dialog>

    <Toast />
  </div>

  <!-- ETAPA 3A: SIN CONEXIÓN Y ESTE EQUIPO NUNCA DESCARGÓ EL CATÁLOGO -->
  <div v-else-if="!cargandoComponente && sinCatalogoOffline" class="p-4">
    <Message severity="warn" :closable="false">
      <div class="flex align-items-center justify-content-between gap-3">
        <span>Sin conexión: este equipo todavía no tiene el catálogo de la sucursal. Conéctese a internet y vuelva a entrar al POS (o abra la caja con conexión).</span>
        <Button label="Reintentar" icon="pi pi-refresh" class="p-button-warning" @click="iniciarPos" />
      </div>
    </Message>
  </div>

  <!-- VISTA CUANDO LA CAJA ESTÁ CERRADA -->
  <div v-else-if="!cargandoComponente && !permitirVenta" class="p-4">
    <div class="grid">
      <Message class="col-12" :severity="'info'" :closable="false">
        <div class="flex align-items-center justify-content-center">
          <span> PARA REALIZAR VENTAS DEBE ABRIR LA CAJA </span>
          <Button
            label="APERTURAR CAJA"
            class="p-button-success p-button-lg ml-3"
            icon="pi pi-lock-open"
            v-tooltip.top="'Abrir Caja'"
            @click="this.$router.push({ name: 'caja' })"
          />
        </div>
      </Message>
    </div>
  </div>

  <!-- VISTA MIENTRAS VERIFICA LA CAJA -->
  <div v-else class="p-4">
    <div class="grid text-center">
      <h3 class="col-12 text-700">Verificando estado de la caja...</h3>
      <ProgressSpinner
        style="width: 50px; height: 50px"
        strokeWidth="8"
        fill="var(--surface-ground)"
        animationDuration=".5s"
        class="col-12"
      />
    </div>
  </div>
</template>

<script>

import { computed } from 'vue';
import { useStockPolling } from '@/composables/useStockPolling';
import { useImpresora } from '@/composables/useImpresora';
// IMPORTACIÓN DE LOS COMPONENTES DESDE TUS CARPETAS ATOMIC DESIGN
import ClienteService from '@/service/ClienteService'; // NUEVO: Importación ClienteService
import VentaService from '@/service/VentaService';
import { normalizarComplementos, totalesCarrito, redondear2, claveCatalogo, unicosPorClave, precioCanal } from '@/helpers/posCarrito';
import { fetchWrapper } from '@/helpers/fetchWrapper';
import { useAuth, useConexion } from '@/stores';
import { descargarCatalogo, leerCatalogo, leerCatalogoDeMiCaja, refrescarProductos, olvidarCajaDelCatalogo } from '@/services-offline/catalogoLocal';
import { encolarVenta, reactivarVenta, actualizarControl, descartarVenta } from '@/services-offline/colaVentas';
import { sincronizarAhora } from '@/services-offline/sincronizador';
import ZapiEstadoSync from '@/components/pos/ZapiEstadoSync.vue';
import { nuevoUuid } from '@/helpers/uuid';
import { fechaIsoConOffset } from '@/helpers/fechaLocal';
import { armarTicket, ticketAEscPosBase64, envolverTexto } from '@/helpers/ticketLocal';
import { VENTA_ONLINE } from '@/config/offline';
import { urlImagen, resolverUrlsImagenes, revocarUrls } from '@/services-offline/imagenesLocal';
import { promoVigenteHoy, promoDelCanal, promoSoportadaOffline } from '@/helpers/promosPos';
import ZapiHeader from './ventas/view/VentaPOS/components/organisms/ZapiHeader.vue';
import ZapiCategoryTabs from './ventas/view/VentaPOS/components/molecules/ZapiCategoryTabs.vue';
import ZapiProductCard from './ventas/view/VentaPOS/components/molecules/ZapiProductCard.vue';
import ZapiClientForm from './ventas/view/VentaPOS/components/molecules/ZapiClientForm.vue';
import ZapiOriginSelector from './ventas/view/VentaPOS/components/molecules/ZapiOriginSelector.vue';
import ZapiPaymentSelector from './ventas/view/VentaPOS/components/molecules/ZapiPaymentSelector.vue';
import ZapiCartTable from './ventas/view/VentaPOS/components/organisms/ZapiCartTable.vue';
import ZapiModalPizzaConfig from './ventas/view/VentaPOS/components/organisms/ZapiModalPizzaConfig.vue';
import ZapiModalNumpad from './ventas/view/VentaPOS/components/organisms/ZapiModalNumpad.vue';
import ZapiModalConfirmacionVenta from './ventas/view/VentaPOS/components/organisms/ZapiModalConfirmacionVenta.vue';
import ZapiModalSmartCombo from './ventas/view/VentaPOS/components/organisms/ZapiModalSmartCombo.vue';
import ClienteCreate from '@/module/clientes/ClienteCreate.vue'; // MODAL PARA CREAR CLIENTES

const API = process.env.VUE_APP_ROOT_API;

export default {
  name: 'VentasPOS',
  components: {
    ZapiHeader,
    ZapiCategoryTabs,
    ZapiProductCard,
    ZapiClientForm,
    ZapiOriginSelector,
    ZapiPaymentSelector,
    ZapiCartTable,
    ZapiModalPizzaConfig,
    ZapiModalNumpad,
    ZapiModalConfirmacionVenta,
    ZapiModalSmartCombo,
    ZapiEstadoSync,
    ClienteCreate
  },
  setup() {
    const { 
      estado: estadoImpresora, 
      listaImpresoras, 
      mostrarModal: mostrarModalImpresora,
      impresoraSeleccionada,
      verificarConexion: verificarConexionImpresora,
      buscarImpresoras,
      guardarImpresora,
      imprimir,
      imprimirTicketVentaBackend,
      imprimirBase64Local,
      probarImpresionBackend
    } = useImpresora();

    return {
      estadoImpresora,
      listaImpresoras,
      mostrarModalImpresora,
      impresoraSeleccionada,
      verificarConexionImpresora,
      buscarImpresoras,
      guardarImpresora,
      imprimir,
      imprimirTicketVentaBackend,
      imprimirBase64Local,
      probarImpresionBackend
    };
  },
  data() {
    return {
      // Estados globales de búsqueda y cliente
      busquedaProducto: null,
      productosFiltrados: [],
      clienteSeleccionado: null,
      clientesFiltrados: [],
      nitCliente: '',

      // Lógica de Canales y Métodos solicitados con v-model reactivo
      canalVenta: 1,
      metodosDisponibles: [],
      metodoPago: null,
      observacionVenta: '',
      descuentoManual: null,

      // Control del Carrito global
      carrito: [],

      // Control de Modales de flujo
      mostrarModalPizza: false,
      pizzaEnEdicion: null,
      
      // Control Smart Combo
      mostrarModalSmartCombo: false,
      promoActivaId: null,

      // NUEVO: Bandera para saber qué índice exacto del carrito se está editando
      // Si es null, significa que estamos agregando una pizza nueva. Si tiene un número, estamos editando.
      indiceEdicionSalsas: null,

      mostrarNumpad: false,
      indiceNumpad: null,
      cantidadTemporal: '1',

      mostrarModalConfirmacion: false,

      mostrarModalCliente: false,
      clienteParaModal: {},

      // Catálogo de Productos Completo
      categoriaActiva: 'Pizza',
      catalogoProductos: [],
      // Bordes y extras de la sucursal (solo para el modal de smart combo; no van a la grilla)
      complementosPos: [],
      // Etapa 3A: canales del catálogo local (para ZapiOriginSelector) y equipo sin catálogo sin conexión
      canalesPos: null,
      sinCatalogoOffline: false,
      // 3A.4: { url del servidor: blob: URL } de las imágenes guardadas en Dexie
      urlsImagenesLocales: {},

      // Control de Grupos/Categorías
      gruposNombres: [],
      rawGruposData: [],
      subCategoriaActiva: null,

      // Información de sesión / apertura
      caja: null,
      sucursal_id: null,
      empresaGlobal: null, // NUEVO: Almacena el logo y datos de la empresa principal
      encabezadoTicket: null, // Ítem 20: encabezado del ticket (bytes ESC/POS con logo) de la sucursal, del catálogo
      logoImpresora: null, // NUEVO: Logo convertido a PNG para evitar errores en la impresora
      cargandoComponente: true,
      permitirVenta: false,

      // Prevención de doble clic
      procesando: false,
      // Etapa 3B: { uuid, firma } de la venta rechazada por el servidor; si se reintenta sin cambios, se reusa el uuid
      ventaEnCurso: null,
      stockEnTiempoReal:{},
      productosIds: [], //Para saber que productos fueron agregados

      // Promociones dinámicas
      evaluandoPromociones: false
    }
  },
  computed: {
    canalesPermitidosCarrito() {
      let permitidos = null;
      this.carrito.forEach(item => {
        if (item.es_smart_combo && item.canales_id && item.canales_id.length > 0) {
          if (permitidos === null) {
            permitidos = [...item.canales_id];
          } else {
            permitidos = permitidos.filter(id => item.canales_id.includes(id));
          }
        }
      });
      return permitidos;
    },
    // Etapa 3A: lo que se ofrece en la grilla. Los smart combos se filtran por vigencia de hoy y canal
    // (online y offline); sin conexión, además, se ocultan los de tipos no soportados (Etapa 8).
    catalogoVisible() {
      return this.catalogoProductos.filter(p => !p.es_smart_combo || (
        promoVigenteHoy(p) && promoDelCanal(p, this.canalVenta) && (!this.conexion.modoOffline || promoSoportadaOffline(p))
      ));
    },
    // Etapa 3A: la promo del smart combo que se está armando, tal como viene en el catálogo
    // (el modal muestra la imagen desde imagen_pos, que en el catálogo se llama imagen)
    promoActiva() {
      const promo = this.catalogoProductos.find(p => p.es_smart_combo && p.id === this.promoActivaId);
      return promo ? { ...promo, imagen_pos: promo.imagen } : null;
    },
    // Catálogo del modal de smart combo: productos del POS + bordes y extras
    catalogoCombos() {
      return [...this.catalogoProductos, ...this.complementosPos];
    },
    // Calculamos el descuento en base a los combos armados
    descuentoTotalPromocional() {
      // descuento_neto es el descuento de la línea (el backend lo resta una vez por línea)
      return totalesCarrito(this.carrito).descuento;
    },
    desglosePromociones() {
      const desglose = [];
      this.carrito.forEach(item => {
        if (item.descuento_neto > 0 && item.nombre_paquete) {
          const existente = desglose.find(d => d.promocion === item.nombre_paquete);
          if (existente) {
            existente.monto = redondear2(existente.monto + redondear2(item.descuento_neto));
          } else {
            desglose.push({
              nombre_producto: item.nombre,
              promocion: item.nombre_paquete,
              monto: redondear2(item.descuento_neto)
            });
          }
        }
      });
      return desglose;
    },
    // Cálculo reactivo centralizado de totales de la venta
    totalCalculado() {
      return totalesCarrito(this.carrito).subtotal;
    },
    // Total final con descuento manual aplicado y promos dinámicas
    totalAPagar() {
      return totalesCarrito(this.carrito, this.descuentoManual).total;
    },
    // Filtro reactivo para renderizar solo los productos de la categoría seleccionada
    /* productosPorCategoria() {
      return this.catalogoProductos.filter(prod => prod.tipoMenu === this.categoriaActiva && prod.tipoMenu !== 'NO DEFINIDO');
    }, */
    // NUEVO: Productos con stock actualizado para el grid
    subgruposNombres() {
      if (!this.categoriaActiva || !this.rawGruposData) return [];
      const grupoActivo = this.rawGruposData.find(g => g.gru_nombre === this.categoriaActiva);
      if (grupoActivo && grupoActivo.subgrupos && grupoActivo.subgrupos.length > 0) {
        return grupoActivo.subgrupos.map(s => s.sub_nombre);
      }
      return [];
    },
    productosPorCategoria() {
      // Si hay búsqueda, buscamos globalmente en el catálogo general
      if (this.busquedaProducto && this.busquedaProducto.trim() !== '') {
        const filtrados = this.catalogoVisible.filter(prod => {
          if (prod.tipoMenu === 'NO DEFINIDO') return false;
          return prod.nombre.toLowerCase().includes(this.busquedaProducto.toLowerCase());
        });

        // DESDUPLICACIÓN por tipo:id: evita repetidos del backend sin perder un paquete o una
        // promo que comparta id con un producto.
        const unicosPorId = unicosPorClave(filtrados);

        return unicosPorId.map(prod => {
          const stockActual = this.obtenerStockProducto ? this.obtenerStockProducto(prod.id, prod.es_paquete) : null
          return {
            ...prod,
            imagen: this.urlsImagenesLocales[prod.imagen] || prod.imagen, // 3A.4: blob local si existe
            stock_actual: stockActual !== null ? stockActual : prod.stock_actual,
            disponible: stockActual !== null ? stockActual > 0 : true
          }
        });
      }

      // Si no hay búsqueda, mostramos los productos del grupo o subgrupo activo desde el catalogo local
      const grupoActivo = this.rawGruposData.find(g => g.gru_nombre === this.categoriaActiva);
      if (!grupoActivo) return [];

      let productosBrutos = [];
      if (this.subgruposNombres.length > 0 && this.subCategoriaActiva) {
        const subActivo = grupoActivo.subgrupos.find(s => s.sub_nombre === this.subCategoriaActiva);
        if (subActivo) {
          // Filtrar del catalogoProductos: coincida grupo_id y subgrupo_id
          productosBrutos = this.catalogoVisible.filter(p => p.grupo_id === grupoActivo.gru_id && p.subgrupo_id === subActivo.sub_id);
        }
      } else {
        // Si el grupo tiene subgrupos (ej. Gaseosas -> 500ml), pero estamos en la pestaña principal, mostramos los que no tienen subgrupo (ej. sueltos).
        // Si el grupo NO tiene subgrupos (ej. Combos), mostramos todos los de ese grupo.
        if (grupoActivo.subgrupos && grupoActivo.subgrupos.length > 0) {
            productosBrutos = this.catalogoVisible.filter(p => p.grupo_id === grupoActivo.gru_id && (!p.subgrupo_id || p.subgrupo_id == 0));
        } else {
            productosBrutos = this.catalogoVisible.filter(p => p.grupo_id === grupoActivo.gru_id);
        }
      }

      return productosBrutos.map(p => {
        const stockActual = this.obtenerStockProducto ? this.obtenerStockProducto(p.id, p.es_paquete) : null;
        return {
          ...p, // Ya tiene el formato id, nombre, precioBase, ingredientes, etc.
          imagen: this.urlsImagenesLocales[p.imagen] || p.imagen, // 3A.4: blob local si existe
          stock_actual: stockActual !== null ? stockActual : (p.stock_actual !== undefined ? p.stock_actual : 999),
          disponible: stockActual !== null ? stockActual > 0 : true
        }
      });
    },
    modalProductoSalsa() {
      return this.catalogoProductos.filter(prod => {
        const hasExtrasValue = prod.has_extras !== undefined ? prod.has_extras : prod.stock?.[0]?.has_extras;
        const hasExtras = ['1', 1, true, 'true'].includes(hasExtrasValue);
        // Sin conexión el stock del caché puede estar viejo: se ofrecen todas las salsas (decisión 6)
        const hasStock = this.conexion.modoOffline || Number(prod.stock_actual) > 0;

        return hasExtras && hasStock;
      });
    },

  },
  watch: {
    canalesPermitidosCarrito(newPermitidos) {
      if (newPermitidos && newPermitidos.length > 0) {
        if (!newPermitidos.includes(this.canalVenta)) {
          this.canalVenta = newPermitidos[0];
        }
      }
    },
    carrito: {
      handler() {
        this.evaluarPromocionesDinamicas();
        this.conexion.carritoConProductos = this.carrito.length > 0; // Etapa 6: confirmación de «Actualizar»
      },
      deep: true
    },
    categoriaActiva() {
      // Limpiar la búsqueda global si el usuario hace clic en una pestaña de categoría
      this.busquedaProducto = null;

      if (this.subgruposNombres.length > 0) {
        this.subCategoriaActiva = this.subgruposNombres[0];
      } else {
        this.subCategoriaActiva = null;
      }
    },
    subCategoriaActiva() {
      // Limpiar la búsqueda global si el usuario hace clic en una pestaña de subcategoría
      this.busquedaProducto = null;
    },
    canalVenta() {
      this.recalcularPreciosCarrito();
      this.evaluarPromocionesDinamicas();
    }
  },
  created() {
    this.clienteService = new ClienteService();
    this.ventaService = new VentaService();
    // Etapa 3A: estado de la conexión (el logo de la empresa ahora llega con el catálogo, en aplicarCatalogo)
    this.conexion = useConexion();
    // Etapa 3C: sin conexión el polling de stock se pausa (solo daría un error cada 5 s); al volver, se reanuda.
    // Va en created con $watch (no en `watch`): this.conexion no existe todavía cuando se crean los watch del componente.
    this.$watch(() => this.conexion.modoOffline, (offline) => {
      if (offline) { if (this._detenerPolling) this._detenerPolling(); }
      else if (this._iniciarPolling) this._iniciarPolling();
    });
  },
  mounted() {
    this.iniciarPos();
    this.iniciarPollingStock();
    this.verificarConexionImpresora(true);
    // Etapa 3D (D5): al cerrar o recargar la pestaña, el navegador pregunta solo si hay productos en el carrito
    // (lo único que se pierde: las ventas cobradas ya están en la cola local)
    this._avisoAlSalir = (event) => {
      if (!this.conexion.carritoConProductos) return;
      event.preventDefault();
      event.returnValue = ''; // el texto lo pone el navegador
    };
    window.addEventListener('beforeunload', this._avisoAlSalir);
  },
  // Etapas 3A y 3C: al salir del POS se detiene el polling de stock y se liberan las URLs de las imágenes locales
  beforeUnmount() {
    if (this._detenerPolling) this._detenerPolling();
    revocarUrls(this.urlsImagenesLocales);
    this.conexion.carritoConProductos = false;
    window.removeEventListener('beforeunload', this._avisoAlSalir); // Etapa 3D (D5)
  },
  methods: {
    claveCatalogo(item) {
      return claveCatalogo(item);
    },
    getImageUrl(img) {
      // Etapa 3A: misma regla que imagenesLocal (corrige el doble prefijo de las imágenes data:image…)
      return urlImagen(img);
    },
    async convertirLogoAPng(base64Original) {
      return new Promise((resolve) => {
        if (!base64Original) { resolve(null); return; }
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 300;
          let width = img.width;
          let height = img.height;
          
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          
          const dataUrl = canvas.toDataURL('image/png');
          const base64Png = dataUrl.replace(/^data:image\/png;base64,/, "");
          resolve(base64Png);
        };
        img.onerror = () => {
          console.error("Error al convertir el logo a PNG");
          resolve(base64Original);
        };
        const src = base64Original.startsWith('data:') ? base64Original : 'data:image/jpeg;base64,' + base64Original;
        img.src = src;
      });
    },
    // Etapa 3A: arranque del POS. Online: caja del servidor + catálogo descargado. Sin red: caja y catálogo de Dexie.
    async iniciarPos() {
      this.cargandoComponente = true;
      this.sinCatalogoOffline = false;
      // Etapa 3B: el banner muestra las ventas en cola también después de recargar la pestaña
      this.conexion.refrescarContadoresCola().catch(e => console.warn('Contadores de la cola:', e));
      // 3A.5: pedir al navegador que no borre la base local del POS por falta de espacio
      if (navigator.storage && navigator.storage.persist) {
        // Etapa 3D (D6): el resultado va al store (línea gris en el diálogo «Ventas»). Antes:
        // navigator.storage.persist().then((ok) => { if (!ok) console.warn('El navegador no garantizó el almacenamiento local del POS'); });
        navigator.storage.persist().then((ok) => {
          this.conexion.almacenamientoPersistente = ok;
          if (!ok) console.warn('El navegador no garantizó el almacenamiento local del POS');
        });
      }
      try {
        let caja = null;
        let fila = null;
        let sinRespuesta = this.conexion.modoOffline;
        if (!sinRespuesta) {
          const r = await fetchWrapper.raw('GET', `${API}/caja/verificar`, null, { timeoutMs: 8000 });
          if (r.status === 401) { useAuth().userLogout(); return; } // al entrar todavía no hay carrito que perder
          if (r.ok) {
            caja = (r.data.caja || [])[0] || null;
            if (!caja) {
              // Respuesta real del servidor: no hay caja abierta (como antes)
              this.permitirVenta = false;
              this.$toast.add({ severity: 'warn', summary: 'Aviso', detail: 'No tienes una caja abierta. Debes abrir caja para facturar.', life: 5000 });
              return;
            }
            // Online siempre se descarga: es lo que pinta la grilla (una sola descarga, con todas_las_vigencias=1)
            fila = await descargarCatalogo(caja.sucursal_id) || await leerCatalogo(caja.sucursal_id);
          } else {
            // Ítem 21 (limite_429_resultado.md): un 429 (límite de peticiones) o cualquier 5xx NO dicen que la caja esté
            // cerrada: el POS sigue por el camino sin red (catálogo local y caja guardada) y lo avisa. Antes solo
            // 0/502/503/504, y un 429 o un 500 acababan en «PARA REALIZAR VENTAS DEBE ABRIR LA CAJA». Antes:
            // sinRespuesta = r.status === 0 || [502, 503, 504].includes(r.status);
            const servidorOcupado = r.status === 429 || r.status >= 500;
            sinRespuesta = r.status === 0 || servidorOcupado;
            if (servidorOcupado) {
              // group 'avisos-3d': el toast del banner, siempre montado (el del POS aún no existe mientras carga)
              this.$toast.add({
                group: 'avisos-3d', severity: 'warn', summary: 'Servidor ocupado',
                detail: `El servidor está ocupado (HTTP ${r.status}): trabajando con datos locales. Las ventas se guardan y se envían solas.`,
                life: 8000,
              });
            }
          }
        }
        if (sinRespuesta) {
          fila = await leerCatalogoDeMiCaja();
          caja = fila ? fila.caja : null;
        }
        if (!caja || !fila) {
          this.permitirVenta = false;
          this.sinCatalogoOffline = sinRespuesta; // 3A.6: mensaje claro, no pantalla en blanco
          return;
        }
        this.caja = caja;
        this.sucursal_id = caja.sucursal_id;
        this.aplicarCatalogo(fila);
        this.permitirVenta = true;
        // 3C.5: al fijar sucursal_id, el watch de useStockPolling arranca el polling en el siguiente tick.
        // Si se entró ya sin conexión, se detiene después de ese arranque (no antes, o lo volvería a encender).
        await this.$nextTick();
        if (this.conexion.modoOffline && this._detenerPolling) this._detenerPolling();
      } catch (error) {
        console.error("Error al iniciar el POS:", error);
        this.permitirVenta = false;
      } finally {
        this.cargandoComponente = false;
      }
    },
    evaluarPromocionesDinamicas() {
      // DESACTIVADO POR PETICIÓN DEL CLIENTE: 
      // Las promociones ya no se evalúan automáticamente enviando al backend.
      // Ahora se calculan directamente en las propiedades computadas `descuentoTotalPromocional`
      // basadas en lo que el usuario haya seleccionado explícitamente en el modal de Smart Combos.
      return;
    },
    // Etapa 3A: pinta el POS con una fila del catálogo (recién descargada o de Dexie)
    aplicarCatalogo(fila) {
      // Grupos: filtrar activos (estado != 0), omitir "NO DEFINIDO" y seleccionar el primero por defecto
      this.rawGruposData = Array.isArray(fila.grupos) ? fila.grupos : [];
      this.gruposNombres = this.rawGruposData.filter(g => g.estado != 0 && g.gru_nombre !== 'NO DEFINIDO').map(g => g.gru_nombre);
      if (this.gruposNombres.length > 0 && !this.gruposNombres.includes(this.categoriaActiva)) {
        this.categoriaActiva = this.gruposNombres[0];
      }
      this.aplicarProductos(fila.productos);
      // Bordes y extras de la sucursal para el modal de smart combo
      this.complementosPos = normalizarComplementos(fila.bordes || [], fila.extras || []);
      this.canalesPos = fila.canales || [];
      // Logo de la empresa para el ticket (antes se pedía a /empresa en created)
      this.empresaGlobal = { logo: fila.empresa_logo };
      // Ítem 20: encabezado de ticket (logo + cabecera) que guarda el catálogo, para el ticket local sin conexión
      this.encabezadoTicket = fila.encabezado_ticket || null;
      if (fila.empresa_logo) {
        this.convertirLogoAPng(fila.empresa_logo).then(png => { this.logoImpresora = png; });
      }
      // 3A.4: imágenes guardadas en Dexie como URLs de objeto (si no hay blob se usa la URL del servidor)
      revocarUrls(this.urlsImagenesLocales);
      resolverUrlsImagenes(fila.productos).then(mapa => { this.urlsImagenesLocales = mapa; });
    },
    aplicarProductos(productos) {
      this.catalogoProductos = (productos || []).map(prod => ({
        ...prod,
        nombre: prod.descripcion || prod.nombre,
        ingredientes: prod.caso_uso || prod.ingredientes,
        es_paquete: prod.es_paquete || false,
        precioBase: parseFloat(prod.precio_venta || prod.precioBase || 0),
        tipoMenu: prod.grupo_nombre || prod.tipoMenu,
        imagen: this.getImageUrl(prod.imagen)
      }));
    },
    buscarProducto(event) {
      const resultados = this.catalogoVisible.filter(p => p.nombre.toLowerCase().includes(event.query.toLowerCase()));
      // DESDUPLICACIÓN por tipo:id: evita sugerencias repetidas en el buscador de la cabecera
      this.productosFiltrados = unicosPorClave(resultados);
    },
    buscarCliente(event) {
      if (!this.clienteService) return;
      this.clienteService.buscarXNombre({ texto: event.query, sucursal_id: this.sucursal_id })
        .then((response) => {
          if (response && response.clientes) {
            this.clientesFiltrados = response.clientes;
          } else {
            this.clientesFiltrados = [];
          }
        })
        .catch(err => console.error("Error buscando cliente:", err));
    },
    /* recalcularPreciosCarrito() {
       Forzar que Vue recalcule las propiedades computadas que dependan del carrito
      this.carrito = [...this.carrito]; 
    }, */

    // CONFIRMACIÓN DEL ZapiModalSmartCombo
    confirmarSmartCombo({ selecciones, promo }) {
      this.mostrarModalSmartCombo = false;
      this.promoActivaId = null;
      
      // 1. Crear un identificador único para agrupar los items de este combo
      const comboGroupId = 'smartcombo_' + Date.now();

      // 2. Agregar el "paquete abstracto" al carrito para que el usuario sepa que es una promo,
      // pero con precio 0. Sus items hijos tendrán los precios que luego el motor descontará.
      this.carrito.push({
        id: promo.id,
        nombre: `Combo: ${promo.nombre}`,
        es_smart_combo: true,
        combo_group_id: comboGroupId,
        canales_id: promo.canales_id,
        precioBase: 0,
        cantidad: 1,
        stock_actual: 999
      });

      // 3. Agregar los productos seleccionados al carrito, vinculados a ese combo
      selecciones.forEach(prod => {
        // Buscamos si necesita configuración (Ej. pizza con bordes o extra)
        // En una v2 se podría abrir el modal de configuración en cadena, 
        // pero por ahora lo agregamos directo.
        this.carrito.push({
          ...prod,
          cantidad: 1,
          combo_group_id: comboGroupId, // Referencia al combo padre
          // Ocultamos la eliminación individual para que si borran un ítem, deban borrar todo el combo? 
          // (opcional, por ahora lo agregamos normal)
        });
      });

      this.recalcularPreciosCarrito();
      this.$toast.add({ severity: 'success', summary: 'Combo Armado', detail: 'Los productos se agregaron al carrito.', life: 3000 });
    },

    // NO SE USA: nadie llama a este método y cargarImpresoras() no existe (useImpresora devuelve buscarImpresoras). Se deja comentado por si se necesita; la función real es buscarImpresoras().
    // abrirModalImpresora() {
    //   this.cargarImpresoras();
    //   this.mostrarModalImpresora = true;
    // },

    seleccionarCliente(cliente) {      
      this.clienteSeleccionado = cliente;
      this.nitCliente = cliente.ci_nit || '0';
    },
    abrirModalCliente() {
      this.mostrarModalCliente = true;
      let nombrePrellenado = '';
      if (this.clienteSeleccionado) {
        nombrePrellenado = this.clienteSeleccionado.nombre || (typeof this.clienteSeleccionado === 'string' ? this.clienteSeleccionado : '');
      }
      this.clienteParaModal = {
        estado: { label: "Activo", value: 1 },
        nombre: nombrePrellenado,
        tipo_cliente: 0
      };
    },
    cerrarModalCliente() {
      this.mostrarModalCliente = false;
      this.clienteParaModal = {};
    },
    actualizarListadoClientes(nuevoCliente) {
      this.seleccionarCliente(nuevoCliente);
      this.cerrarModalCliente();
    },
    // MODIFICADO: Agregar al carrito con validación de stock
    seleccionarPizza(pizza) {
      // 1. Interceptar si es un Smart Combo
      if (pizza.es_smart_combo) {
        this.promoActivaId = pizza.id; // El 'id' de la promoción que el backend inyectó
        this.mostrarModalSmartCombo = true;
        return;
      }

      // Verificar stock antes de agregar
      let stockActual = this.obtenerStockProducto(pizza.id, pizza.es_paquete);
      if (stockActual === null) {
        stockActual = pizza.stock_actual;
      }

      // Si tiene stock, proceder
      if (pizza.requiere_modal === true) {
        // Inicializamos el objeto base de la pizza
        const nuevaPizza = {
          ...pizza,
          cantidad: 1,
          stock_actual: stockActual // Guardar stock en el objeto
        };
        
        // Inyectamos a 1 todas las salsas dinámicas encontradas por el filtro
        this.modalProductoSalsa.forEach(salsa => {
          nuevaPizza['salsa_' + salsa.id] = 1;
        });

        this.pizzaEnEdicion = nuevaPizza;
        this.mostrarModalPizza = true;
      } else {
        // Las líneas de un smart combo (padre e hijos) nunca se fusionan con productos sueltos
        const existente = this.carrito.find(item => !item.combo_group_id && item.id === pizza.id && item.es_paquete === pizza.es_paquete);
        if (existente) {
          existente.cantidad += 1;
        } else {
          this.carrito.push({ ...pizza, cantidad: 1, stock_actual: stockActual });
        }
      }
      this.recalcularPreciosCarrito();
    },
    // EVENTO DESDE ZapiCartTable: Se ejecuta al tocar el lápiz en la tabla
    abrirEdicionSalsas(index) {
      this.indiceEdicionSalsas = index;

      // Clonamos el objeto de la fila usando Destructuring (...). 
      // Esto previene que si el cajero cambia algo y luego le da a "Cancelar", el carrito original se haya modificado.
      this.pizzaEnEdicion = { ...this.carrito[index] };
      this.mostrarModalPizza = true;
    },

    // CONFIRMACIÓN DEL MODAL ZapiModalPizzaConfig
    confirmarAgregarAlCarrito(pizzaConfigurada) {
      // Guardamos valores originales para que la edición repetida no corrompa los datos
      if (pizzaConfigurada._precioOriginal === undefined) {
         pizzaConfigurada._precioOriginal = pizzaConfigurada.precioBase;
      }
      if (!pizzaConfigurada._nombreOriginal) {
         pizzaConfigurada._nombreOriginal = pizzaConfigurada.nombre;
      }
      
      pizzaConfigurada.nombre = pizzaConfigurada._nombreOriginal;

      if (pizzaConfigurada.es_mixta) {
        pizzaConfigurada.nombre = `Mixta de ${pizzaConfigurada._nombreOriginal} y ${pizzaConfigurada.pizza_mitad_2.nombre}`;
        pizzaConfigurada.nombre_paquete = pizzaConfigurada.nombre; // Para el comprobante backend
      }

      // Verificar stock antes de confirmar
      let stockActual = this.obtenerStockProducto(pizzaConfigurada.id, pizzaConfigurada.es_paquete)
      if (stockActual === null) {
        stockActual = pizzaConfigurada.stock_actual;
      }
      
      if (this.indiceEdicionSalsas !== null) {
        // --- MODO EDICIÓN ---
        // Reemplazamos la fila exacta del carrito con el nuevo objeto editado
        this.carrito[this.indiceEdicionSalsas] = { ...pizzaConfigurada };
        this.$toast.add({ severity: 'success', summary: 'Actualizado', detail: 'Configuración modificada.', life: 2000 });

        // 🧹 Limpiamos la bandera de edición para futuras interacciones
        this.indiceEdicionSalsas = null;
      } else {
        // --- MODO AGREGAR NUEVO ---
        // Lógica de colisión: Verificamos si la pizza tiene exactamente las mismas salsas que otra que ya está en el carrito
        const existente = this.carrito.find(item => {
          if (item.combo_group_id) return false; // no fusionar con líneas de un smart combo
          if (item.id !== pizzaConfigurada.id) return false;
          if (item.es_paquete !== pizzaConfigurada.es_paquete) return false;
          
          // Verificamos si coinciden en ser mixtas y su segunda mitad
          if (Boolean(item.es_mixta) !== Boolean(pizzaConfigurada.es_mixta)) return false;
          if (item.es_mixta && item.pizza_mitad_2?.id !== pizzaConfigurada.pizza_mitad_2?.id) return false;

          // Verificamos los bordes seleccionados
          const bordesItem = item.bordes_seleccionados || [];
          const bordesConfig = pizzaConfigurada.bordes_seleccionados || [];
          if (bordesItem.length !== bordesConfig.length) return false;
          
          const bordesItemIds = bordesItem.map(b => b.id).sort().join(',');
          const bordesConfigIds = bordesConfig.map(b => b.id).sort().join(',');
          if (bordesItemIds !== bordesConfigIds) return false;

          // Verificamos los extras seleccionados
          const extrasItem = item.extras_seleccionados || [];
          const extrasConfig = pizzaConfigurada.extras_seleccionados || [];
          if (extrasItem.length !== extrasConfig.length) return false;
          
          const extrasItemIds = extrasItem.map(e => e.id).sort().join(',');
          const extrasConfigIds = extrasConfig.map(e => e.id).sort().join(',');
          if (extrasItemIds !== extrasConfigIds) return false;

          // Verificamos dinámicamente si todas las salsas coinciden
          let coincidenSalsas = true;
          this.modalProductoSalsa.forEach(salsa => {
            const key = 'salsa_' + salsa.id;
            if ((item[key] || 0) !== (pizzaConfigurada[key] || 0)) {
              coincidenSalsas = false;
            }
          });
          
          return coincidenSalsas;
        });


        if (existente) {
          // Si colisiona, simplemente agrupamos sumando las cantidades
          existente.cantidad += pizzaConfigurada.cantidad;
        } else {
          // Si es una combinación única, la empujamos como nueva fila al carrito
          this.carrito.push({ ...pizzaConfigurada });
        }
        this.$toast.add({ severity: 'success', summary: 'Agregado', detail: `${pizzaConfigurada.nombre} al pedido.`, life: 2000 });
      }

      this.recalcularPreciosCarrito();

      // Cerramos el modal de pizzas y limpiamos la variable temporal
      this.mostrarModalPizza = false;
      this.pizzaEnEdicion = null;
    },
    eliminarDelCarrito(index, combo_group_id) {
      if (combo_group_id) {
        // Eliminar todos los ítems asociados a este combo
        this.carrito = this.carrito.filter(item => item.combo_group_id !== combo_group_id);
      } else {
        this.carrito.splice(index, 1);
      }
      this.recalcularPreciosCarrito();
    },
    sumarCantidadCarrito(index) {
      this.carrito[index].cantidad++;
    },
    restarCantidadCarrito(index) {
      if (this.carrito[index].cantidad > 1) {
        this.carrito[index].cantidad--;
      } else {
        // Si baja de 1, eliminamos del carrito para mantenerlo limpio
        this.eliminarDelCarrito(index);
      }
    },
    abrirNumpad(index) {
      this.indiceNumpad = index;
      this.cantidadTemporal = String(this.carrito[index].cantidad);
      this.mostrarNumpad = true;
    },
    confirmarNumpad(cantidadEmitida) {
      const nuevaCantidad = parseInt(cantidadEmitida, 10);
      if (nuevaCantidad > 0) {
        this.carrito[this.indiceNumpad].cantidad = nuevaCantidad;
        this.mostrarNumpad = false;
        this.$toast.add({ severity: 'success', summary: 'Actualizado', detail: 'Cantidad modificada.', life: 2000 });
      } else {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'La cantidad debe ser mayor a 0.', life: 2000 });
      }
    },
    abrirModalMerma() {
      this.$toast.add({ severity: 'info', summary: 'Módulo Merma', detail: 'Abriendo registro rápido de control de mermas e insumos...', life: 3000 });
    },
    
    actualizarMetodosPago(metodosDb) {
      if (!metodosDb) return;
      
      // Los únicos 3 métodos que queremos mostrar en la interfaz
      const metodosFijos = [
        { label: 'Efectivo', value: 1, icon: 'pi pi-money-bill' },
        { label: 'Tarjeta', value: 2, icon: 'pi pi-credit-card' },
        { label: 'Pago QR', value: 6, icon: 'pi pi-qrcode' },
        { label: 'Pago Online', value: 4, icon: 'pi pi-desktop' }
      ];

      // Filtramos para mostrar SOLO los botones que están permitidos por el backend
      // AHORA: Mostramos siempre los 3, pero bloqueamos (disabled) los que no están permitidos
      this.metodosDisponibles = metodosFijos.map(fijo => ({
        ...fijo,
        disabled: !metodosDb.some(db => db.id === fijo.value)
      }));

      // Obtenemos cuántos de los botones "visuales" (los fijos) quedaron habilitados
      const habilitadosVisibles = this.metodosDisponibles.filter(m => !m.disabled);

      // Si el canal de venta SOLO tiene 1 método en base de datos (Ej: PedidosYa -> Transferencia id:4)
      // Asignamos el ID interno para que el backend lo registre correctamente.
      if (metodosDb.length === 1) {
        this.metodoPago = metodosDb[0].id;
      } 
      // Si hay botones visibles habilitados, seleccionamos el primero de ellos por defecto
      else if (habilitadosVisibles.length > 0) {
        const existe = habilitadosVisibles.find(m => m.value === this.metodoPago);
        if (!existe) {
          this.metodoPago = habilitadosVisibles[0].value;
        }
      } else {
        this.metodoPago = null;
      }
    },

    /**
     * Recalcula dinámicamente los precios de todos los productos en el carrito de compras.
     * Este método garantiza que cada ítem refleje el precio correcto basado en el 
     * canal de venta seleccionado actualmente en el POS, siguiendo el mapeo estricto del negocio:
     * 
     * - Canal 1 (Local) / Canal 5 (WhatsApp): Utilizan el `precio_venta` o `precioBase` normal.
     * - Canal 2 (PedidosYa): Utiliza la tarifa preferencial `pv3` de la base de datos.
     * - Canal 3 (Yango): Utiliza la tarifa preferencial `pv2` de la base de datos.
     * - Canal 4 (Turbo): Utiliza la tarifa preferencial `pv4` de la base de datos.
     * - Canal 6 (Cortesía): Utiliza la tarifa preferencial `pv5` de la base de datos.
     * 
     * @observación Si el producto no tiene una tarifa especial configurada para un canal 
     * externo (ej. la base de datos devuelve "0.00"), el sistema actúa con un "fallback" seguro 
     * manteniendo el `precioBase` original para evitar cobrar 0 Bs por un producto.
     * 
     * @returns {void} Muta directamente el estado reactivo de `this.carrito`, actualizando la propiedad `precioFinal` de cada ítem.
     */
    recalcularPreciosCarrito() {
      this.carrito.forEach(item => {
        // Asegurar que guardamos el precio original del catálogo la primera vez
        if (item._precioOriginal === undefined) {
          item._precioOriginal = item.precioBase;
        }

        let precioFinal = parseFloat(item._precioOriginal || 0);

        /**
         * MULTIPLICADOR DE COMBOS:
         * Si el ítem es un combo (paquete), extraemos `cantidad_personalizable` que viene desde la BD.
         * Este valor (ej: 2) nos dice exactamente cuántas pizzas/hamburguesas reales hay adentro.
         * Ignora productos estáticos como sodas. Si no es paquete, el multiplicador es 1.
         * @type {number}
         */
        let multiplicador = item.es_paquete ? (item.cantidad_personalizable || 1) : 1;

        // Aplicar regla de precios por canal de venta, si es 0 mantener el precio base
        if (this.canalVenta === 3) {
          let p = parseFloat(item.pv2);
          if (p > 0) precioFinal = p;
        }
        else if (this.canalVenta === 2) {
          let p = parseFloat(item.pv3);
          if (p > 0) precioFinal = p;
        }
        else if (this.canalVenta === 4) {
          let p = parseFloat(item.pv4);
          if (p > 0) precioFinal = p;
        }
        else if (this.canalVenta === 6) {
          let p = parseFloat(item.pv5);
          if (!isNaN(p)) precioFinal = p; // Acepta 0
        }

        // Si es pizza mixta, promediamos con la segunda mitad
        if (item.es_mixta && item.pizza_mitad_2) {
          let precioMitad2 = parseFloat(item.pizza_mitad_2._precioOriginal || item.pizza_mitad_2.precioBase || item.pizza_mitad_2.precio_venta || 0);
          
          precioMitad2 = precioCanal(item.pizza_mitad_2, this.canalVenta, precioMitad2);

          precioFinal = (precioFinal + precioMitad2) / 2;
        }

        item._precioCanal = precioFinal; // Guardar el precio base limpio sin sumarle los extras

        // Sumar complementos como bordes
        if (item.bordes_seleccionados && item.bordes_seleccionados.length > 0) {
          item.bordes_seleccionados.forEach(b => {
             let precioBorde = parseFloat(b.precio_venta || 0);
             precioBorde = precioCanal(b, this.canalVenta, precioBorde);
             
             precioFinal += (precioBorde * multiplicador);
          });
        }

        // Sumar complementos como extras
        if (item.extras_seleccionados && item.extras_seleccionados.length > 0) {
          item.extras_seleccionados.forEach(e => {
             let precioExtra = parseFloat(e.precio_venta || 0);
             precioExtra = precioCanal(e, this.canalVenta, precioExtra);
             
             precioFinal += (precioExtra * multiplicador);
          });
        }

        item.precioBase = precioFinal;
      });
    },

    prepararVenta() {
      this.mostrarModalConfirmacion = true;
    },
    async procesarVenta(montosConfirmados) {
      if (this.procesando) return;
      this.procesando = true;
      try {
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        const sucursalActiva = this.sucursal_id || (user ? user.sucursal_id : 1);

        // 1. Mapeo de Productos para el backend (Estructura minimalista para PosController)
        const productosPayload = [];
        const paquetesPayload = [];
        
        this.carrito.forEach(item => {
          if (item.es_smart_combo) return;

          // Extraemos los complementos (ej. Salsas) que tenga este item
          const complementosItem = [];

          /**
           * MULTIPLICADOR PARA ENVÍO A BD:
           * Este multiplicador define con cuánta "cantidad" se va a enviar el extra a la cocina y BD.
           * Ej: Borde de Queso, cantidad = 2 (Si el combo tenía 2 pizzas personalizables).
           */
          let multiplicador = item.es_paquete ? (item.cantidad_personalizable || 1) : 1;

          // Agregamos los bordes primero para que aparezcan justo debajo del producto
          if (item.bordes_seleccionados && item.bordes_seleccionados.length > 0) {
            item.bordes_seleccionados.forEach(b => {
              let precioBorde = parseFloat(b.precio_venta || 0);
              precioBorde = precioCanal(b, this.canalVenta, precioBorde);

              complementosItem.push({
                producto_id: b.id,
                cantidad: item.es_paquete ? multiplicador : item.cantidad,
                precio: precioBorde
              });
            });
          }

          // Agregamos los extras
          if (item.extras_seleccionados && item.extras_seleccionados.length > 0) {
            item.extras_seleccionados.forEach(e => {
              let precioExtra = parseFloat(e.precio_venta || 0);
              precioExtra = precioCanal(e, this.canalVenta, precioExtra);
              
              complementosItem.push({
                producto_id: e.id,
                cantidad: item.es_paquete ? multiplicador : item.cantidad,
                precio: precioExtra
              });
            });
          }

          this.modalProductoSalsa.forEach(salsa => {
            const cantSalsa = item['salsa_' + salsa.id] || 0;
            if (cantSalsa > 0) {
              complementosItem.push({
                producto_id: salsa.id,
                // OJO: El backend multiplica los complementos de paquetes por la cantidad del paquete,
                // pero NO multiplica los de productos sueltos. Ajustamos aquí para enviar lo correcto:
                cantidad: item.es_paquete ? (cantSalsa * multiplicador) : (cantSalsa * item.cantidad)
              });
            }
          });

          if (item.es_mixta && item.pizza_mitad_2) {
            complementosItem.push({
              producto_id: item.pizza_mitad_2.id,
              cantidad: item.cantidad
            });
          }

          if (item.es_paquete) {
            paquetesPayload.push({
              paquete_id: item.id,
              cantidad: item.cantidad,
              precio_unitario: item._precioCanal !== undefined ? item._precioCanal : parseFloat(item.precioBase),
              complementos: complementosItem
            });
          } else {
            productosPayload.push({
              producto_id: item.id,
              cantidad: item.cantidad,
              precio: item._precioCanal !== undefined ? item._precioCanal : parseFloat(item.precioBase),
              descuento_neto: parseFloat(item.descuento_neto) || 0,
              lote: "0",
              fecha_vence: null,
              complementos: complementosItem,
              nombre_paquete: item.nombre_paquete || null,
              es_mixta: item.es_mixta || false,
              es_paquete: item.es_paquete || false,
              // Hijo de smart combo: qué promoción lo originó (el backend toma el nombre de la base)
              promotion_id: item.promotion_id || null,
              nombre_promocion: item.nombre_promocion || null
            });
          }
        });

        // Ahora this.metodoPago ya contiene el ID real de la base de datos (Ej: 1, 2, 4, 6)
        const pagoId = this.metodoPago || 1;

        let efectivoVenta = 0;
        let montoPagoVenta = 0;

        if (montosConfirmados) {
          efectivoVenta = montosConfirmados.efectivo || 0;
          montoPagoVenta = montosConfirmados.monto_pago || 0;
        } else {
          // Fallback por si acaso
          if (pagoId === 1) {
            efectivoVenta = this.totalAPagar;
            montoPagoVenta = 0;
          } else {
            efectivoVenta = 0;
            montoPagoVenta = this.totalAPagar;
          }
        }

        // 2. Construcción del Payload (Estructura idéntica a VentasCrear.vue)
        const payload = {
          cliente_id: this.clienteSeleccionado ? (this.clienteSeleccionado.id || this.clienteSeleccionado.cliente_id) : 1, // 1 = Cliente General
          razon_social: this.clienteSeleccionado ? this.clienteSeleccionado.nombre : 'Cliente General',
          nit: this.nitCliente || '0',
          total_cancelar: this.totalAPagar,
          descuento_manual: parseFloat(this.descuentoManual) || 0,
          // Se elimina "descuento: 0" para que el backend aplique su cálculo automático en combos
          efectivo: efectivoVenta,
          productos: productosPayload,
          paquetes: paquetesPayload,
          caja_id: this.caja ? this.caja.id : 1,
          tipo_pago_id: 1, // 1 = CONTADO (Fijo para el POS)
          metodo_pago_id: pagoId, // 1 = Efectivo, 2 = Tarjeta, 6 = QR
          monto_pago: montoPagoVenta,
          dias_credito: 0,
          a_cuenta: 0,
          cambio: 0,
          facturado: 0,
          observacion: this.observacionVenta || 'Venta rápida desde POS',
          nombre_paquete: null,
          nro_tarjeta: null,
          proforma: false,
          interes_venta_credito: 0,
          monto_interes_venta_credito: 0,
          venta_credito: false,
          vehiculo_id: null,
          canal_venta_id: this.canalVenta, // AQUÍ GUARDAMOS EL CANAL DE VENTA SELECCIONADO
          fact_indicador: 0,
          proforma_id: null,
          sucursal_id: sucursalActiva,
          mostrar_fecha_venta: false,
          fecha_venta: null,
          venta_fecha_diferente: false
        };        
        // Etapa 3B: uuid y fecha del equipo en TODAS las ventas; la venta se guarda en la cola ANTES de enviarla.
        // Reintento tras un rechazo sin cambios en la venta (misma firma): mismo uuid y mismo registro de la cola.
        const firma = this.firmaVenta(payload);
        const reusar = this.ventaEnCurso !== null && this.ventaEnCurso.firma === firma;
        payload.uuid_venta = reusar ? this.ventaEnCurso.uuid : nuevoUuid();
        // Etapa 3D (D3): hora corregida solo con el equipo adelantado ≥ 30 s (ver conexion.fechaVentaCorregida). Antes:
        // payload.fecha_venta_cliente = fechaIsoConOffset(new Date());
        const { fecha, fechaEquipo, desfaseAplicadoMs } = this.conexion.fechaVentaCorregida();
        payload.fecha_venta_cliente = fechaIsoConOffset(fecha);
        const auditoriaFecha = { fecha_equipo: fechaIsoConOffset(fechaEquipo), desfase_aplicado_ms: desfaseAplicadoMs };
        this.ventaEnCurso = { uuid: payload.uuid_venta, firma };
        const ticket = this.armarTicketLocal(payload, user);
        let registro = null;
        try {
          // Etapa 3D (D3): antes sin auditoriaFecha en reactivarVenta y encolarVenta
          registro = (reusar && await reactivarVenta(payload.uuid_venta, { payload, ticket, fecha_venta_cliente: payload.fecha_venta_cliente, ...auditoriaFecha }))
            || await encolarVenta({
              uuid_venta: payload.uuid_venta, caja_id: payload.caja_id, sucursal_id: payload.sucursal_id,
              user_id: user ? user.id : null, fecha_venta_cliente: payload.fecha_venta_cliente, ...auditoriaFecha, payload, ticket
            });
        } catch (e) {
          console.error('No se pudo guardar la venta en la cola local:', e);
          if (this.conexion.modoOffline) {
            this.$toast.add({ severity: 'error', summary: 'Error', detail: 'No se puede guardar la venta en este equipo.', life: 6000 });
            return;
          }
        }

        if (this.conexion.modoOffline) {
          await this.finalizarVentaLocal(registro, 'Venta guardada sin conexión');
          return;
        }

        const res = await fetchWrapper.raw('POST', `${API}/pos/ventas/procesar`, payload, { timeoutMs: VENTA_ONLINE.timeoutMs });
        const datos = res.data && typeof res.data === 'object' ? res.data : {}; // un 502 del proxy puede traer HTML
        const mensaje = datos.message || '';
        // 2026-10: mensajes de stock con nombre. QUÉ/POR QUÉ: el servidor manda la causa real en `error` pero solo se
        // mostraba `message`; se deja en consola para soporte. No cambia el flujo de la venta ni el sync offline.
        // Relacionado: backend ProcesarVentaService::logErrorRegistrar (la misma causa queda en laravel.log).
        // La causa técnica (datos.error) no se muestra al cajero: queda en la consola para quien soporta el POS.
        // Los rechazos de negocio (p. ej. sin stock) ya traen el nombre del producto en `message`.
        if (!res.ok && datos.error) console.warn('Venta rechazada por el servidor:', datos.error);
        // D3: en los 409 manda el `codigo`; el texto del mensaje solo si la respuesta no lo trae
        const es409 = (codigoEsperado, mensajeEsperado) => res.status === 409
          && (datos.codigo ? datos.codigo === codigoEsperado : mensaje === mensajeEsperado);

        if (res.ok && datos.success) {
          if (registro) await actualizarControl(registro.uuid_venta, { estado: 'sincronizada', venta_id: datos.venta ? datos.venta.id : null, sincronizada_en: new Date().toISOString() });
          await this.finalizarVentaOnline(datos);
        } else if (es409(VENTA_ONLINE.codigoDuplicada, VENTA_ONLINE.mensajeDuplicada)) {
          // Un intento anterior con el mismo uuid ya entró (su respuesta se perdió)
          if (registro) await actualizarControl(registro.uuid_venta, { estado: 'sincronizada', ultimo_error: 'duplicada', sincronizada_en: new Date().toISOString() });
          await this.finalizarVentaLocal(registro, 'La venta ya estaba registrada', 'sincronizada');
        } else if (res.status === 429) {
          // Ítem 21 (D1, limite_429_resultado.md): límite de peticiones. Un 429 garantiza que el servidor NO la registró:
          // va a la cola como «sin respuesta» (mismo uuid_venta, ticket local) y el sync la envía con backoff. Antes caía
          // en la rama de 4xx/500 de abajo: descartada (no cobrada) y el cajero tenía que volver a cobrar.
          await this.finalizarVentaLocal(registro, 'Servidor ocupado: la venta se guardó y se enviará sola');
          sincronizarAhora(); // si sigue ocupado, el sync aplica el backoff (5 s, 10 s, … hasta 5 min)
        } else if (res.status === 0 || [502, 503, 504].includes(res.status)) {
          await this.finalizarVentaLocal(registro, 'Sin respuesta del servidor: la venta quedó guardada y se enviará sola');
          sincronizarAhora(); // 3C: la reenvía en cuanto el servidor responda (el uuid evita duplicarla)
        } else if (res.status === 401) {
          this.conexion.sesionExpirada = true; // D7: a la cola, sin cerrar sesión ni perder el carrito
          await this.finalizarVentaLocal(registro, 'Sesión vencida: la venta quedó guardada y se enviará al volver a iniciar sesión');
        } else {
          // 4xx y 500 (D6): el servidor respondió y NO registró la venta → no se cobra, el carrito queda
          if (registro) await descartarVenta(registro.uuid_venta, mensaje || `HTTP ${res.status}`);
          if (es409(VENTA_ONLINE.codigoCajaCerrada, VENTA_ONLINE.mensajeCajaCerrada)) {
            // Sin esto, el POS sin red seguiría creyendo que la caja está abierta
            await olvidarCajaDelCatalogo(payload.sucursal_id).catch(e => console.warn('No se pudo actualizar la caja del catálogo local:', e));
          }
          this.$toast.add({ severity: 'error', summary: 'Error al procesar', detail: mensaje || 'No se pudo completar la operación.', life: 6000 });
        }
      } catch (err) {
        console.error("Error en procesarVenta:", err);
        const errorMsg = err.response && err.response.data && err.response.data.message ? err.response.data.message : 'No se pudo completar la operación.';
        this.$toast.add({ severity: 'error', summary: 'Error', detail: errorMsg, life: 5000 });
      } finally {
        this.procesando = false;
        this.mostrarModalConfirmacion = false;
      }
    },
    // Etapa 3B: reset del carrito tras una venta cobrada (lo mismo que hacía antes el bloque de éxito)
    // (todas las salidas de una venta completada pasan por aquí: también se descarta el uuid de ventaEnCurso)
    limpiarVenta() {
      this.ventaEnCurso = null;
      this.carrito = [];
      this.canalVenta = 1;
      this.metodoPago = 1;
      this.observacionVenta = '';
      this.clienteSeleccionado = null;
      this.nitCliente = '';
      this.descuentoManual = null;
    },
    // Etapa 3B: venta registrada por el servidor (200): toast, carrito limpio, stock y ticket del servidor
    async finalizarVentaOnline(datos) {
      this.$toast.add({ severity: 'success', summary: 'Venta Procesada', detail: datos.message || 'El pedido se ha registrado exitosamente.', life: 4000 });
      this.limpiarVenta();
      // Etapa 3A: solo productos (y se guardan también en el catálogo local)
      const productos = await refrescarProductos(this.sucursal_id);
      if (productos) this.aplicarProductos(productos);

      if (datos.venta && datos.venta.id) {
        try {
          const impresoConExito = await this.imprimirTicketVentaBackend(datos.venta.id);

          if (impresoConExito) {
            // Imprimir copia extra idéntica para la cocina
            await this.imprimirTicketVentaBackend(datos.venta.id);
          }

          if (!impresoConExito) {
            const pdfBlob = await this.ventaService.obtenerPdfVenta(datos.venta.id);
            const url = URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');
          }
        } catch (error) {
          this.$toast.add({ severity: 'error', summary: 'Error de impresión', detail: error.message || 'Error al imprimir ticket', life: 5000 });
        }
      }
    },
    // Etapa 3B: venta cobrada sin respuesta del servidor (o sin conexión): ticket local ×2 y carrito limpio
    async finalizarVentaLocal(registro, mensaje, estadoTicket = 'pendiente') {
      if (!registro && estadoTicket === 'sincronizada') {
        // Duplicada sin registro local: el servidor ya la tiene, no hay ticket local que imprimir
        this.limpiarVenta();
        this.$toast.add({ severity: 'info', summary: 'Venta registrada', detail: mensaje, life: 6000 });
        return;
      }
      if (!registro) {
        // Sin cola local (IndexedDB no disponible) no hay ticket ni reenvío: como antes, el carrito queda
        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Sin respuesta del servidor y no se pudo guardar la venta en este equipo. Revise el listado de ventas antes de volver a cobrar.', life: 8000 });
        return;
      }
      const base64 = ticketAEscPosBase64(registro.ticket, estadoTicket);
      const impreso = await this.imprimirBase64Local(base64);
      if (impreso) await this.imprimirBase64Local(base64); // copia para cocina, como el ticket del servidor
      this.limpiarVenta();
      this.$toast.add({ severity: estadoTicket === 'sincronizada' ? 'info' : 'warn', summary: 'Venta guardada',
        detail: `${mensaje} (${this.conexion.ventasEnCola} en cola).`, life: 6000 });
    },
    // Etapa 3B: lo que define la venta. Si cambia el carrito, el canal, el método de pago, el cliente o el descuento,
    // es otra venta (uuid nuevo). Efectivo, monto_pago y observación no cuentan: corregirlos es reintentar la misma.
    firmaVenta(payload) {
      return JSON.stringify([payload.productos, payload.paquetes, payload.canal_venta_id, payload.metodo_pago_id,
        payload.cliente_id, payload.razon_social, payload.nit, payload.descuento_manual, payload.total_cancelar]);
    },
    // Etapa 3B: datos planos del ticket local (se guardan inmutables en la cola)
    armarTicketLocal(payload, user) {
      /* Antes (ítem 20, parte A): todas las líneas del carrito, también las del 2x1, salían una por una:
      const lineas = this.carrito.filter(i => !i.es_smart_combo).map(i => ({
        cantidad: i.cantidad,
        nombre: i.nombre,
        precio: parseFloat(i.precioBase) || 0,
        total: redondear2((parseFloat(i.precioBase) || 0) * i.cantidad),
        detalle: [
          ...(i.es_mixta && i.pizza_mitad_2 ? [`Mitad: ${i.pizza_mitad_2.nombre}`] : []),
          ...(i.bordes_seleccionados || []).map(b => `Borde: ${b.nombre || b.descripcion}`),
          ...(i.extras_seleccionados || []).map(e => e.nombre || e.descripcion),
          ...this.modalProductoSalsa.filter(s => i['salsa_' + s.id] > 0).map(s => `${i['salsa_' + s.id]} x ${s.nombre}`),
          ...(i.nombre_paquete ? [i.nombre_paquete] : []),
          // Etapa 8 (D8): qué unidad salió con descuento («2x1 -60.00»; en los demás combos, «Desc. -X»). El total
          // de la línea sigue sin descuento: el descuento se suma en DESC. al pie, como antes.
          ...(Number(i.descuento_neto) > 0 ? [`${i.etiqueta_promo || 'Desc.'} -${redondear2(i.descuento_neto).toFixed(2)}`] : []),
        ],
      }));
      */
      /* Antes (ítem 20, parte A): solo se agrupaba el 2x1 «mismo producto» (líneas con etiqueta_promo); los combos normales
         salían componente por componente:
      // Ítem 20 (parte A, etapa_8_promos.md): las líneas de un mismo 2x1 «mismo producto» (las que llevan etiqueta_promo y
      // comparten combo_group_id) salen en UNA sola línea, con el formato del ticket del backend (ImpresionService):
      // cantidad 1, «Combo: <promo>» con P.UNIT y TOTAL = suma de las líneas, y debajo «(<productos>)». Sin «x2» y sin
      // «2x1 -X» por línea (el descuento ya va en DESC.). Combos normales y productos sueltos: igual que antes.
      const lineaSuelta = (i) => ({
        cantidad: i.cantidad,
        nombre: i.nombre,
        precio: parseFloat(i.precioBase) || 0,
        total: redondear2((parseFloat(i.precioBase) || 0) * i.cantidad),
        detalle: [
          ...(i.es_mixta && i.pizza_mitad_2 ? [`Mitad: ${i.pizza_mitad_2.nombre}`] : []),
          ...(i.bordes_seleccionados || []).map(b => `Borde: ${b.nombre || b.descripcion}`),
          ...(i.extras_seleccionados || []).map(e => e.nombre || e.descripcion),
          ...this.modalProductoSalsa.filter(s => i['salsa_' + s.id] > 0).map(s => `${i['salsa_' + s.id]} x ${s.nombre}`),
          ...(i.nombre_paquete ? [i.nombre_paquete] : []),
          // Etapa 8 (D8): en los demás combos, «Desc. -X» (el total de la línea sigue sin descuento: el descuento va en DESC.)
          ...(Number(i.descuento_neto) > 0 ? [`${i.etiqueta_promo || 'Desc.'} -${redondear2(i.descuento_neto).toFixed(2)}`] : []),
        ],
      });
      const gruposMismoProducto = {};
      const lineas = [];
      this.carrito.filter(i => !i.es_smart_combo).forEach(i => {
        if (i.combo_group_id && i.etiqueta_promo) {
          let g = gruposMismoProducto[i.combo_group_id];
          if (!g) {
            g = { nombre: i.nombre_paquete || 'Combo', total: 0, componentes: [] };
            gruposMismoProducto[i.combo_group_id] = g;
            g.item = { cantidad: 1, nombre: g.nombre, precio: 0, total: 0, envolver: true, detalle: [] };
            lineas.push(g.item);
          }
          g.total = redondear2(g.total + (parseFloat(i.precioBase) || 0) * i.cantidad);
          if (!g.componentes.includes(i.nombre)) g.componentes.push(i.nombre); // nombres únicos, como el backend
          g.item.precio = g.total; // cantidad 1: P.UNIT = TOTAL = suma de las líneas
          g.item.total = g.total;
          g.item.detalle = envolverTexto('(' + g.componentes.join(' + ') + ')', 42);
          return;
        }
        lineas.push(lineaSuelta(i));
      });
      */
      // Ítem 20 (ticket_local_resultado.md): TODAS las líneas de un mismo combo (2x1 o combo normal: comparten combo_group_id)
      // salen en UNA sola línea, con el formato del ticket del backend (ImpresionService): cantidad de la primera línea,
      // «Combo: <nombre>» con P.UNIT = total / cantidad y TOTAL = suma de las líneas; debajo, los productos sin repetir entre
      // paréntesis («(A + B)»), y los bordes/extras indentados sin precio. Sin «x2» y sin «Desc. -X» por línea (el descuento
      // ya va en DESC.). Productos sueltos: igual que antes.
      const lineaSuelta = (i) => ({
        cantidad: i.cantidad,
        nombre: i.nombre,
        precio: parseFloat(i.precioBase) || 0,
        total: redondear2((parseFloat(i.precioBase) || 0) * i.cantidad),
        detalle: [
          ...(i.es_mixta && i.pizza_mitad_2 ? [`Mitad: ${i.pizza_mitad_2.nombre}`] : []),
          ...(i.bordes_seleccionados || []).map(b => `Borde: ${b.nombre || b.descripcion}`),
          ...(i.extras_seleccionados || []).map(e => e.nombre || e.descripcion),
          ...this.modalProductoSalsa.filter(s => i['salsa_' + s.id] > 0).map(s => `${i['salsa_' + s.id]} x ${s.nombre}`),
          ...(i.nombre_paquete ? [i.nombre_paquete] : []),
          // Etapa 8 (D8): «Desc. -X» (el total de la línea sigue sin descuento: el descuento va en DESC.)
          ...(Number(i.descuento_neto) > 0 ? [`${i.etiqueta_promo || 'Desc.'} -${redondear2(i.descuento_neto).toFixed(2)}`] : []),
        ],
      });
      const gruposCombo = {};
      const lineas = [];
      this.carrito.filter(i => !i.es_smart_combo).forEach(i => {
        if (i.combo_group_id) {
          let g = gruposCombo[i.combo_group_id];
          if (!g) {
            g = { total: 0, componentes: [], adicionales: [], item: { cantidad: i.cantidad, nombre: i.nombre_paquete || 'Combo', precio: 0, total: 0, envolver: true, detalle: [] } };
            gruposCombo[i.combo_group_id] = g;
            lineas.push(g.item);
          }
          g.total = redondear2(g.total + (parseFloat(i.precioBase) || 0) * i.cantidad);
          const nombreMin = String(i.nombre || '').toLowerCase();
          // como el backend: ni «cobro paquete» ni salsas aparecen en la lista de productos del combo; nombres únicos
          if (!nombreMin.includes('cobro paquete') && !nombreMin.includes('salsa') && !g.componentes.includes(i.nombre)) g.componentes.push(i.nombre);
          (i.bordes_seleccionados || []).forEach(b => g.adicionales.push(`   Borde: ${b.nombre || b.descripcion}`));
          (i.extras_seleccionados || []).forEach(e => g.adicionales.push(`   ${e.nombre || e.descripcion}`));
          g.item.precio = redondear2(g.total / (g.item.cantidad || 1)); // P.UNIT = total / cantidad
          g.item.total = g.total;
          g.item.detalle = [...(g.componentes.length ? envolverTexto('(' + g.componentes.join(' + ') + ')', 42) : []), ...g.adicionales];
          return;
        }
        lineas.push(lineaSuelta(i));
      });
      const totales = totalesCarrito(this.carrito, this.descuentoManual);
      const canal = (this.canalesPos || []).find(c => Number(c.id) === Number(this.canalVenta));
      const metodo = this.metodosDisponibles.find(m => m.value === this.metodoPago);
      return armarTicket({
        items: lineas, subtotal: totales.subtotal,
        descuento: redondear2(totales.subtotal - totales.total), // promos + descuento manual
        total: totales.total,
        totalEnLetras: this.numeroALetras(totales.total),
        cliente: payload.razon_social, nit: payload.nit,
        canal: canal ? canal.nombre : String(this.canalVenta), metodoPago: metodo ? metodo.label : String(this.metodoPago),
        caja: payload.caja_id, sucursal: (this.caja && this.caja.nombre_sucursal) || '',
        // Etapa 3D (H4): la misma hora que la venta. Antes: fecha: new Date().toLocaleString('es-BO'),
        cajero: user ? user.name : '', fecha: new Date(payload.fecha_venta_cliente).toLocaleString('es-BO'),
        // Ítem 20: glosa (la observación de la venta) y encabezado del ticket (bytes ESC/POS en base64; null si el catálogo no lo trae)
        glosa: payload.observacion || '',
        encabezado: this.encabezadoTicket ? this.encabezadoTicket.escpos_base64 || null : null,
      });
    },
    iniciarPollingStock(){
      //crear un ref reactive para el carrito
      const carritoRef = computed(() => this.carrito)
      const sucursalRef = computed(() => this.sucursal_id)

      const { stockActualizado, detener, iniciar, obtenerStock } = useStockPolling(
        carritoRef,
        sucursalRef,
        { interval: 5000, autoStart: true }
      )
      this.stockEnTiempoReal = stockActualizado
      this.obtenerStockTiempoReal = obtenerStock      
      // Guardar referencia para limpiar
      this._detenerPolling = detener
      this._iniciarPolling = iniciar
    },

    // NUEVO: Obtener stock en tiempo real para un producto
    obtenerStockProducto(productoId, es_paquete) {
      if (!this.obtenerStockTiempoReal) return null
      return this.obtenerStockTiempoReal(productoId, es_paquete)
    },
    
    // NUEVO: Función para convertir números a letras (equivalente a NumberFormatter::SPELLOUT)
    numeroALetras(numero) {
      const Unidades = ['CERO', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE', 'DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE', 'VEINTE', 'VEINTIUNO', 'VEINTIDOS', 'VEINTITRES', 'VEINTICUATRO', 'VEINTICINCO', 'VEINTISEIS', 'VEINTISIETE', 'VEINTIOCHO', 'VEINTINUEVE'];
      const Decenas = ['CERO', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
      const Centenas = ['CERO', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

      function NumeroALetrasParcial(num) {
        if (num < 30) return Unidades[num];
        if (num < 100) return Decenas[Math.floor(num / 10)] + (num % 10 !== 0 ? ' Y ' + Unidades[num % 10] : '');
        if (num === 100) return 'CIEN';
        if (num < 1000) return Centenas[Math.floor(num / 100)] + (num % 100 !== 0 ? ' ' + NumeroALetrasParcial(num % 100) : '');
        if (num === 1000) return 'MIL';
        if (num < 2000) return 'MIL ' + (num % 1000 !== 0 ? NumeroALetrasParcial(num % 1000) : '');
        if (num < 1000000) return NumeroALetrasParcial(Math.floor(num / 1000)) + ' MIL' + (num % 1000 !== 0 ? ' ' + NumeroALetrasParcial(num % 1000) : '');
        if (num === 1000000) return 'UN MILLON';
        if (num < 2000000) return 'UN MILLON ' + (num % 1000000 !== 0 ? NumeroALetrasParcial(num % 1000000) : '');
        return NumeroALetrasParcial(Math.floor(num / 1000000)) + ' MILLONES' + (num % 1000000 !== 0 ? ' ' + NumeroALetrasParcial(num % 1000000) : '');
      }

      const entero = Math.floor(numero);
      const centavos = Math.round((numero - entero) * 100);
      const centavosStr = centavos.toString().padStart(2, '0');
      
      const letrasEntero = NumeroALetrasParcial(entero);
      return `${letrasEntero} ${centavosStr}/100 BOLIVIANOS`;
    }
  }
}
</script>

<style scoped>
/* ==========================================================================
   HOVERS Y ESTILOS DE INTERFAZ DEL ORQUESTADOR (ZAPI PALETA DE MARCA)
   ========================================================================== */
.zapi-bg {
  background-color: #fbf9f8;
  /* Crema institucional */
}

/* Contenedor del POS a pantalla completa; con la franja «Modo Offline» empieza debajo de ella */
.zapi-pos-pantalla {
  top: 0;
  height: 100vh;
}

body.modo-offline .zapi-pos-pantalla {
  top: var(--altura-banner-offline);
  height: calc(100vh - var(--altura-banner-offline));
}

.zapi-text-red {
  color: #e32f3a;
  /* Rojo marca */
}

.zapi-text-gold {
  color: #c98a02;
  /* Dorado tipográfico para precios */
}

.zapi-bg-red {
  background-color: #e32f3a !important;
}

.zapi-bg-red:hover {
  background-color: #c2151d !important;
}

.zapi-bg-gold {
  background-color: #d97e0a !important;
  color: #ffffff !important;
}

.zapi-bg-gold:hover {
  background-color: #d97e0a !important;
}

.zapi-font {
  font-family: 'Impact', 'Arial Black', sans-serif;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* ==========================================================================
   CONTROLES EXCLUSIVOS DE VIEWPORT VERTICAL (VH) - EVITA EL DESBORDE DEL TERMINAL
   ========================================================================== */

/* Altura fija de las filas del grid de comidas */
:deep(.item-card-height) {
  height: 24vh;
}

/* ==========================================================================
   SCROLLBAR INVISIBLE Y ESTABLE (EVITA SALTOS VISUALES)
   ========================================================================== */
/* 1. Ocultar físicamente el scroll cuando el mouse NO está adentro, pero RESERVAR el espacio */
.pos-grid-container,
.pos-cart-table-container {
  overflow-y: hidden !important;
  scrollbar-gutter: stable;
  /* Evita que las tarjetas salten cuando aparece la barra */
}

/* 2. Mostrar el scroll cuando el mouse ENTRA al contenedor */
.pos-grid-container:hover,
.pos-cart-table-container:hover {
  overflow-y: auto !important;
  overflow-y: overlay !important;
}

.subgrupo-wrapper {
  background: #FDECC8;
  border-radius: 10px;
  padding: 2px 12px;
  margin-top: 10px;
  margin-bottom: 5px;
}
.subgrupo-label {
  font-size: 13px;
  font-weight: bold;
  color: #B5760B;
  margin-bottom: 2px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 3. Darle color rojo corporativo a la barra en navegadores compatibles */
.pos-grid-container::-webkit-scrollbar,
.pos-cart-table-container::-webkit-scrollbar {
  width: 12px;
}

.pos-grid-container::-webkit-scrollbar-track,
.pos-cart-table-container::-webkit-scrollbar-track {
  background: transparent;
}

.pos-grid-container::-webkit-scrollbar-thumb,
.pos-cart-table-container::-webkit-scrollbar-thumb {
  background: #e32f3a;
  /* Rojo Zapi */
  border-radius: 10px;
}

/* ==========================================================================
   BREAKPOINT PERSONALIZADO PARA MONITORES GRANDES (1600px+)
   ========================================================================== */
@media screen and (min-width: 1400px) {
  .zapi-card-5 {
    flex: 0 0 auto !important;
    width: 20% !important;
  }
}
</style>